import React, { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5295';
const ROLES = ['Admin', 'Assessor', 'Presales', 'CSP', 'Client'];

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Client');
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([
        fetch(`${API}/api/v1/iam/users`).then(r => r.ok ? r.json() : []),
        fetch(`${API}/api/v1/iam/invitations`).then(r => r.ok ? r.json() : []),
      ]);
      setUsers(u || []);
      setInvitations(i || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const sendInvite = async () => {
    setError(null);
    try {
      const res = await fetch(`${API}/api/v1/iam/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail, role: ROLES.indexOf(inviteRole) }),
      });
      if (!res.ok) throw new Error(`Invite failed: ${res.status}`);
      setShowInvite(false);
      setInviteEmail('');
      await refresh();
    } catch (e) { setError(e.message); }
  };

  const revokeInvite = async (id) => {
    if (!confirm('Revoke this invitation?')) return;
    await fetch(`${API}/api/v1/iam/invitations/${id}/revoke`, { method: 'POST' });
    refresh();
  };

  const unassign = async (userId) => {
    if (!confirm('Remove this user from the active tenant?')) return;
    const res = await fetch(`${API}/api/v1/iam/users/${userId}/unassign`, { method: 'POST' });
    if (!res.ok) {
      const text = await res.text();
      setError(text || `Unassign failed: ${res.status}`);
      return;
    }
    refresh();
  };

  return (
    <div style={{ maxWidth: 960, margin: '32px auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A' }}>User management</h2>
        <button className="btn-primary" onClick={() => setShowInvite(true)}>+ Invite user</button>
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 16, background: '#FFEBEE', color: '#C62828', borderRadius: 6, fontSize: 12 }}>
          {error}
        </div>
      )}

      {showInvite && (
        <div style={{ background: '#fff', border: '1px solid #E8ECF0', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1B2A', marginBottom: 12 }}>Invite a user</div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <input
              type="email"
              placeholder="email@example.com"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              style={{ flex: 1, padding: '8px 10px', fontSize: 12, border: '1px solid #E0E4E8', borderRadius: 6 }} />
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              style={{ padding: '8px 10px', fontSize: 12, border: '1px solid #E0E4E8', borderRadius: 6 }}>
              {ROLES.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 10, color: '#7A8899', marginBottom: 12 }}>
            Client invites are emailed via Microsoft Graph B2B. Internal-pool roles (Admin, Assessor, Presales, CSP)
            require the user to already exist in the platform pool.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={() => setShowInvite(false)}>Cancel</button>
            <button className="btn-primary" onClick={sendInvite}>Send invite</button>
          </div>
        </div>
      )}

      <Section title={`Users (${users.length})`}>
        {loading ? <Loading /> : users.length === 0 ? <Empty /> : (
          <Table headers={['Email', 'Display name', 'Role', 'Status', '']}>
            {users.map(u => (
              <tr key={u.userId} style={{ borderTop: '1px solid #F0F2F5' }}>
                <td style={tdStyle}>{u.email}</td>
                <td style={tdStyle}>{u.displayName || '—'}</td>
                <td style={tdStyle}>{ROLES[u.role] ?? u.role}</td>
                <td style={tdStyle}>{u.isDeactivated ? <span style={{ color: '#C62828' }}>Deactivated</span> : 'Active'}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <button className="btn-secondary" style={{ fontSize: 10 }} onClick={() => unassign(u.userId)}>Remove</button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title={`Pending invitations (${invitations.length})`}>
        {invitations.length === 0 ? <Empty msg="No pending invitations." /> : (
          <Table headers={['Email', 'Role', 'Invited', 'Expires', '']}>
            {invitations.map(i => (
              <tr key={i.id} style={{ borderTop: '1px solid #F0F2F5' }}>
                <td style={tdStyle}>{i.email}</td>
                <td style={tdStyle}>{ROLES[i.role] ?? i.role}</td>
                <td style={tdStyle}>{new Date(i.invitedAt).toLocaleDateString()}</td>
                <td style={tdStyle}>{new Date(i.expiresAt).toLocaleDateString()}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <button className="btn-secondary" style={{ fontSize: 10 }} onClick={() => revokeInvite(i.id)}>Revoke</button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
}

const tdStyle = { padding: '8px 10px', fontSize: 11, color: '#0D1B2A' };

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E8ECF0', borderRadius: 10, padding: 16, marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1B2A', marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#F8F9FA' }}>
          {headers.map((h, i) => (
            <th key={i} style={{ padding: '8px 10px', textAlign: i === headers.length - 1 ? 'right' : 'left', color: '#455A64', fontWeight: 600 }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

const Loading = () => <div style={{ fontSize: 11, color: '#7A8899' }}>Loading…</div>;
const Empty = ({ msg = 'No users in this tenant.' }) => <div style={{ fontSize: 11, color: '#7A8899' }}>{msg}</div>;
